/**
 * Monster Onesie — Shipping Protection toggle in the cart drawer.
 *
 * One optional line item, switched on and off from the drawer. On by default;
 * once the shopper turns it off it stays off for the rest of the session.
 *
 * THE RULE THAT SHAPES ALL OF THIS: the cart is the source of truth for
 * whether protection is on, and sessionStorage is the source of truth for
 * whether the shopper has *asked* for it to be off. The component only acts
 * when those two disagree in the one direction it is allowed to fix —
 * "should be there, isn't" — which is what stops the default-on behaviour
 * from becoming a loop that re-adds a line the shopper just removed.
 *
 * Quantity is set with /cart/update.js rather than /cart/add.js, so the line
 * lands at exactly 1 whatever it was before. add.js increments, and an
 * increment is one double-fire away from charging for two.
 *
 * Protection is not OFFERED unless the product is purchasable — see the gate in
 * snippets/monster-cart-protection.liquid. Note that a draft product's variant
 * can still be added to a cart by id (verified 14 Sep: /cart/add.js returns 200
 * while the product is DRAFT), so the card also renders whenever the line is
 * already in the cart, whatever the product status. A charge in the total must
 * never be invisible, and the protection line no longer appears in the cart
 * list at all.
 */

const STORAGE_KEY = 'monster:protection';

/**
 * sessionStorage throws outright in a Safari private window rather than
 * returning null, and the in-app browsers this store's traffic arrives in are
 * exactly where that happens. A shopper whose browser refuses storage gets
 * the default behaviour, not a broken drawer.
 */
function readPreference() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePreference(value) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* Preference is a convenience; losing it must never break the toggle. */
  }
}

class MonsterCartProtection extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.#switch?.addEventListener('click', this.#onToggle);
    this.#info?.addEventListener('click', this.#onInfoClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    const inCart = this.dataset.inCart === 'true';
    const monsters = Number(this.dataset.monsterCount || '0');

    /*
      No monsters left, but the protection line is still there — the shopper
      removed their last onesie. Take it out rather than leaving a cart that
      contains nothing but $4.99 of insurance on nothing. The element renders
      hidden in this state, so this is its only job.
    */
    if (monsters === 0) {
      if (inCart) this.#set(false, { remember: false });
      return;
    }

    // On by default. Acts only in the "should be there, isn't" direction, and
    // never against a shopper who has turned it off.
    if (!inCart && readPreference() !== 'off') {
      this.#set(true, { remember: false });
    }
  }

  disconnectedCallback() {
    this.#switch?.removeEventListener('click', this.#onToggle);
    this.#info?.removeEventListener('click', this.#onInfoClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @returns {HTMLButtonElement | null} */
  get #switch() {
    return this.querySelector('[data-protect-switch]');
  }

  /** @returns {HTMLButtonElement | null} */
  get #info() {
    return this.querySelector('[data-protect-info]');
  }

  /** @returns {HTMLElement | null} */
  get #pop() {
    return this.querySelector('[data-protect-pop]');
  }

  get #isOn() {
    return this.#switch?.getAttribute('aria-checked') === 'true';
  }

  /**
   * The switch is a <button role="switch">, so Enter and Space arrive as
   * clicks for free — no key handling of its own, and no risk of the two
   * disagreeing.
   */
  #onToggle = () => {
    this.#set(!this.#isOn, { remember: true });
  };

  #onInfoClick = (event) => {
    event.stopPropagation();
    this.#setPopover(this.#info?.getAttribute('aria-expanded') !== 'true');
  };

  /** Escape closes the popover and returns focus to the control that opened it. */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#info?.getAttribute('aria-expanded') !== 'true') return;

    this.#setPopover(false);
    this.#info?.focus();
  };

  /** A click anywhere else closes it — including elsewhere in the drawer. */
  #onDocumentClick = (event) => {
    if (this.#info?.getAttribute('aria-expanded') !== 'true') return;
    if (event.target instanceof Node && this.#pop?.contains(event.target)) return;

    this.#setPopover(false);
  };

  /** @param {boolean} open */
  #setPopover(open) {
    if (!this.#pop || !this.#info) return;

    this.#pop.hidden = !open;
    this.#info.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * @param {boolean} on
   * @param {{remember: boolean}} options - Only a real click is remembered.
   *   The automatic add on first render must not write "on", or a later
   *   session-wide default change could not reach a shopper who never
   *   touched the control.
   */
  async #set(on, { remember }) {
    if (this.#busy) return;

    const id = this.dataset.variantId;
    if (!id) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    if (remember) writePreference(on ? 'on' : 'off');

    // Move the switch immediately so the press feels answered. The cart is
    // still the source of truth — the catch below puts this back if the
    // request fails.
    this.#switch?.setAttribute('aria-checked', on ? 'true' : 'false');

    try {
      /*
        Ask for the cart sections in the same request, exactly as the
        configurator's add does. Without this the response carries no markup
        and the drawer repaints from stale HTML — which here would show the
        toggle snapping back to where it was.
      */
      const sectionIds = [];
      for (const node of document.querySelectorAll('cart-items-component')) {
        if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
      }

      const response = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: { [id]: on ? 1 : 0 },
          sections: sectionIds.join(','),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || 'Could not update the cart.');
      }

      await this.#announce(payload);
    } catch (error) {
      /*
        Put the checkbox back where the cart actually is. A toggle that shows
        "on" over a cart with no protection line is worse than no toggle: it
        tells the shopper they are covered when they are not.
      */
      console.error('[monster] shipping protection update failed', error);
      this.#switch?.setAttribute('aria-checked', this.dataset.inCart === 'true' ? 'true' : 'false');
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Publish the change the way the rest of the theme does, so the drawer and
   * the header badge repaint from one event.
   *
   * action is 'update', NOT 'add'. cart-drawer.js auto-opens the drawer on
   * 'add', and this control lives inside the drawer — announcing an add would
   * make switching protection on pop the drawer open again, sometimes over a
   * shopper who had just closed it.
   */
  async #announce(payload) {
    const { CartLinesUpdateEvent } = await import('@shopify/events');

    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    const resolved = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        source: 'monster-cart-protection',
        sections: payload.sections,
      },
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        /*
          `quantity` is required AND must be > 0, even when the change is a
          removal — it describes how many units the change touched, not the
          resulting cart. Both mistakes were made here and both were caught by
          Shopify's own validator, which rejects the whole payload and logs:
            omitted  -> 'expected number, received undefined at lines[0].quantity'
            zero     -> 'Too small: expected number to be >0'
          A rejected payload is silent to the shopper but drops the event for
          every analytics consumer listening.

          Exactly one unit is added or removed, so it is always 1.
        */
        lines: [{ merchandiseId: String(this.dataset.variantId), quantity: 1 }],
        promise: resolved,
      })
    );
  }
}

if (!customElements.get('monster-cart-protection')) {
  customElements.define('monster-cart-protection', MonsterCartProtection);
}
